import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Spinner, Modal, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStar, faClipboardList, faPlus, faEdit, faTrash,
  faSave, faTimes, faCalendarAlt, faCommentAlt, faChartBar,
  faExclamationTriangle, faCheckCircle, faSyncAlt, faTrashAlt,
  faUserTie, faAward, faMedal, faArrowRight, faBolt,
  faSearch, faChevronLeft, faChevronRight, faSort, faSortUp, faSortDown,
  faEraser
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';

// --- Cache Dexie 24h ---
const db = new Dexie('EvaluationEmployesCacheDB');
db.version(1).stores({
  evaluations: 'key, data, timestamp',
  employees: 'key, data, timestamp',
  criteria: 'key, data, timestamp'
});
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedData(store, key) {
  const entry = await db[store].get(key);
  if (entry && Date.now() - entry.timestamp < CACHE_DURATION) {
    return entry.data;
  }
  return null;
}
async function setCachedData(store, key, data) {
  await db[store].put({ key, data, timestamp: Date.now() });
}

const API_EVAL = `${API_CONFIG2.apiUrl}employe/api_evaluation.php`;
const API_EMPLOYES = `${API_CONFIG2.apiUrl}employe/api_employe.php`;

const EvaluationEmployes = ({ managerId }) => {
  const [employees, setEmployees] = useState([]);
  const [criteria, setCriteria] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [currentEval, setCurrentEval] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({
    employe_id: '',
    date_evaluation: new Date().toISOString().slice(0, 10),
    commentaire: '',
    scores: {}
  });
  const [toast, setToast] = useState(null);

  // --- Pagination & recherche ---
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('date_evaluation');
  const [sortOrder, setSortOrder] = useState('desc');

  // --- Recherche employé dans le modal ---
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const employeeDropdownRef = useRef(null);

  const isMounted = useRef(true);
  const fetchInProgress = useRef(false);
  const retryTimeoutRef = useRef(null);

  // Nettoyage
  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, []);

  const showToast = (msg, variant = 'success') => {
    setToast({ msg, variant });
    setTimeout(() => setToast(null), 5000);
  };

  // --- Vidage du cache ---
  const clearCache = async () => {
    setClearingCache(true);
    try {
      await db.evaluations.clear();
      await db.employees.clear();
      await db.criteria.clear();
      showToast('Cache vidé avec succès ✓', 'success');
      // Recharger toutes les données depuis l'API
      await fetchCriteria(true);
      await fetchEmployees(true);
      await fetchEvaluations(true);
    } catch (err) {
      console.error('Erreur vidage cache', err);
      showToast('Erreur lors du vidage du cache', 'danger');
    } finally {
      setClearingCache(false);
    }
  };

  // --- Chargement des critères (avec cache) ---
  const fetchCriteria = useCallback(async (forceRefresh = false, retryCount = 0) => {
    try {
      const cacheKey = 'criteria_list';
      if (!forceRefresh) {
        const cached = await getCachedData('criteria', cacheKey);
        if (cached) {
          setCriteria(cached);
          return;
        }
      }
      const uniqueParam = new Date().getTime();
      const res = await fetch(`${API_EVAL}?action=fetch_criteres&unique=${uniqueParam}`);
      if (res.status === 429) {
        const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
        showToast(`Trop de requêtes. Nouvelle tentative dans ${waitTime/1000}s...`, 'warning');
        retryTimeoutRef.current = setTimeout(() => {
          fetchCriteria(forceRefresh, retryCount + 1);
        }, waitTime);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      if (data.status === 'success') {
        setCriteria(data.data);
        await setCachedData('criteria', cacheKey, data.data);
      } else {
        throw new Error(data.message || 'Erreur');
      }
    } catch (err) {
      console.warn('Fallback critères', err);
      // Fallback avec IDs uniques
      const fallback = [
        { id: 1, libelle: 'Qualité de travail' },
        { id: 2, libelle: 'Productivité' },
        { id: 3, libelle: "Esprit d'équipe" },
        { id: 4, libelle: 'Respect des procédures' },
        { id: 5, libelle: "Innovation" },
        { id: 6, libelle: "Sens des responsabilités" },
        { id: 7, libelle: "Organisation" },
        { id: 8, libelle: "Relation avec les clients" },
        { id: 9, libelle: "Capacité d'adaptation" },
        { id: 10, libelle: "Leadership" },
      ];
      setCriteria(fallback);
      showToast('Utilisation des critères par défaut', 'warning');
    }
  }, []);

  // --- Chargement des employés (tous, sans filtre) ---
  const fetchEmployees = useCallback(async (forceRefresh = false, retryCount = 0) => {
    try {
      // Utiliser une clé de cache explicite pour tous les employés
      const cacheKey = 'all_employees';
      if (!forceRefresh) {
        const cached = await getCachedData('employees', cacheKey);
        if (cached) {
          setEmployees(cached);
          return;
        }
      }
      const uniqueParam = new Date().getTime();
      const res = await fetch(`${API_EMPLOYES}?action=fetch&limit=10000&unique=${uniqueParam}`);
      if (res.status === 429) {
        const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
        showToast(`Trop de requêtes. Nouvelle tentative dans ${waitTime/1000}s...`, 'warning');
        retryTimeoutRef.current = setTimeout(() => {
          fetchEmployees(forceRefresh, retryCount + 1);
        }, waitTime);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      if (data.status === 'success') {
        // On garde tous les employés (pas de filtre)
        setEmployees(data.data);
        await setCachedData('employees', cacheKey, data.data);
      }
    } catch (err) {
      console.error('Erreur chargement employés', err);
      showToast('Erreur de chargement des employés', 'danger');
    }
  }, []);

  // --- Chargement des évaluations (avec cache et gestion 429) ---
  const fetchEvaluations = useCallback(async (forceRefresh = false, retryCount = 0) => {
    if (!managerId) return;
    if (fetchInProgress.current && !forceRefresh) return;
    fetchInProgress.current = true;

    if (!forceRefresh && !refreshing) setLoading(true);

    try {
      const cacheKey = `evaluations_${managerId}`;
      if (!forceRefresh) {
        const cached = await getCachedData('evaluations', cacheKey);
        if (cached) {
          setEvaluations(cached);
          setLoading(false);
          if (!refreshing) {
            setRefreshing(true);
            fetchEvaluations(false, 0);
          }
          fetchInProgress.current = false;
          return;
        }
      }
      const uniqueParam = new Date().getTime();
      const res = await fetch(`${API_EVAL}?action=fetch&userId=${managerId}&limit=10000&unique=${uniqueParam}`);
      if (res.status === 429) {
        if (retryCount >= 3) {
          showToast('Mise à jour impossible (429) – données en cache', 'warning');
          fetchInProgress.current = false;
          setRefreshing(false);
          return;
        }
        const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
        showToast(`Trop de requêtes. Nouvelle tentative dans ${waitTime/1000}s...`, 'warning');
        retryTimeoutRef.current = setTimeout(() => {
          if (isMounted.current) fetchEvaluations(forceRefresh, retryCount + 1);
        }, waitTime);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      if (data.status === 'success') {
        const evaluationsData = Array.isArray(data.data) ? data.data : [];
        setEvaluations(evaluationsData);
        await setCachedData('evaluations', cacheKey, evaluationsData);
        setLoading(false);
        setRefreshing(false);
        showToast('Données actualisées ✓', 'success');
      } else {
        throw new Error(data.message || 'Erreur');
      }
    } catch (err) {
      console.error('Erreur evaluations', err);
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        showToast(err.message || 'Erreur de chargement', 'danger');
      }
    } finally {
      fetchInProgress.current = false;
    }
  }, [managerId, refreshing]);

  // --- Chargement initial ---
  useEffect(() => {
    isMounted.current = true;
    fetchCriteria();
    fetchEmployees();
    fetchEvaluations();

    return () => {
      isMounted.current = false;
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Reset du formulaire ---
  const resetForm = useCallback(() => {
    setForm({
      employe_id: '',
      date_evaluation: new Date().toISOString().slice(0, 10),
      commentaire: '',
      scores: criteria.reduce((acc, c) => ({ ...acc, [c.id]: 0 }), {})
    });
    setCurrentEval(null);
    setEmployeeSearch('');
  }, [criteria]);

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
    setShowEmployeeDropdown(false);
  };

  const openEditModal = (evaluation) => {
    let scores = {};
    try { scores = JSON.parse(evaluation.scores); } catch { scores = {}; }
    const mergedScores = criteria.reduce((acc, c) => ({ ...acc, [c.id]: scores[c.id] || 0 }), {});
    setForm({
      employe_id: evaluation.employe_id,
      date_evaluation: evaluation.date_evaluation,
      commentaire: evaluation.commentaire || '',
      scores: mergedScores
    });
    setCurrentEval(evaluation);
    setShowModal(true);
    setEmployeeSearch('');
    setShowEmployeeDropdown(false);
  };

  // --- Gestion des notes ---
  const handleScoreChange = (critereId, value) => {
    setForm(prev => ({
      ...prev,
      scores: { ...prev.scores, [critereId]: parseInt(value) || 0 }
    }));
  };

  // --- Validation du formulaire ---
  const validateForm = () => {
    if (!form.employe_id || !form.date_evaluation) {
      showToast('Veuillez remplir tous les champs obligatoires', 'warning');
      return false;
    }
    if (Object.values(form.scores).every(v => v === 0)) {
      showToast('Veuillez attribuer au moins une note', 'warning');
      return false;
    }
    return true;
  };

  // --- Soumission (création / mise à jour) ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    const payload = {
      evaluateur_id: managerId,
      employe_id: form.employe_id,
      date_evaluation: form.date_evaluation,
      commentaire: form.commentaire,
      scores: form.scores
    };
    try {
      let res;
      if (currentEval) {
        payload.id = currentEval.id;
        res = await fetch(`${API_EVAL}?action=update`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        res = await fetch(`${API_EVAL}?action=create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }
      const data = await res.json();
      if (data.status !== 'success') throw new Error(data.message);
      showToast(currentEval ? 'Évaluation mise à jour ✓' : 'Évaluation créée ✓', 'success');
      setShowModal(false);
      fetchEvaluations(true);
    } catch (err) {
      showToast(err.message || "Erreur lors de l'enregistrement", 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Suppression ---
  const confirmDelete = (evaluation) => {
    setDeleteTarget(evaluation);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_EVAL}?action=delete&id=${deleteTarget.id}&evaluateur_id=${managerId}`);
      const data = await res.json();
      if (data.status !== 'success') throw new Error(data.message);
      showToast('Évaluation supprimée ✓', 'success');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      fetchEvaluations(true);
    } catch (err) {
      showToast(err.message || 'Erreur lors de la suppression', 'danger');
    } finally {
      setDeleting(false);
    }
  };

  // --- Helpers ---
  const getEmployeeInfo = (id) => employees.find(emp => emp.id == id);

  const renderStars = (note) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <FontAwesomeIcon
          key={i}
          icon={faStar}
          className={i <= note ? 'text-warning' : 'text-muted'}
          style={{ marginRight: 2 }}
        />
      );
    }
    return stars;
  };

  // --- Filtrage et tri des évaluations ---
  const filteredEvaluations = evaluations.filter(evalItem => {
    const emp = getEmployeeInfo(evalItem.employe_id);
    const fullName = emp ? `${emp.prenom} ${emp.nom}` : '';
    return fullName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const sortedEvaluations = [...filteredEvaluations].sort((a, b) => {
    let aVal, bVal;
    if (sortField === 'date_evaluation') {
      aVal = new Date(a.date_evaluation);
      bVal = new Date(b.date_evaluation);
    } else if (sortField === 'note_globale') {
      aVal = a.note_globale || 0;
      bVal = b.note_globale || 0;
    } else {
      const empA = getEmployeeInfo(a.employe_id);
      const empB = getEmployeeInfo(b.employe_id);
      aVal = empA ? `${empA.nom} ${empA.prenom}` : '';
      bVal = empB ? `${empB.nom} ${empB.prenom}` : '';
    }
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const totalItems = sortedEvaluations.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedEvaluations = sortedEvaluations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset page quand la recherche ou les données changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, evaluations]);

  // --- Gestion du tri ---
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // --- Sélecteur d'employé avec recherche (dropdown personnalisé) ---
  const filteredEmployees = employees.filter(emp =>
    `${emp.prenom} ${emp.nom} ${emp.matricule}`
      .toLowerCase()
      .includes(employeeSearch.toLowerCase())
  );

  const selectEmployee = (empId) => {
    setForm(prev => ({ ...prev, employe_id: empId }));
    const emp = employees.find(e => e.id == empId);
    setEmployeeSearch(emp ? `${emp.prenom} ${emp.nom}` : '');
    setShowEmployeeDropdown(false);
  };

  // Gérer la fermeture du dropdown au clic externe
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (employeeDropdownRef.current && !employeeDropdownRef.current.contains(event.target)) {
        setShowEmployeeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- Composant Toast ---
  const ToastMessage = ({ toast }) => {
    if (!toast) return null;
    const variantClass = toast.variant === 'success' ? 'success' : toast.variant === 'warning' ? 'warning' : 'danger';
    const icon = toast.variant === 'success' ? faCheckCircle : faExclamationTriangle;
    return (
      <div className="toast-item">
        <FontAwesomeIcon icon={icon} className={`text-${variantClass} me-3`} />
        <span>{toast.msg}</span>
        <button className="btn-close-custom" onClick={() => setToast(null)}>✕</button>
        <div className="toast-progress" />
      </div>
    );
  };

  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – EVALUATION EMPLOYES
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .eval-container {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: 30px;
          color: var(--text-primary);
          font-family: 'Inter', sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: 1.8rem;
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .monetra-btn {
          padding: 10px 20px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          cursor: pointer;
          font-size: 0.9rem;
          white-space: nowrap;
        }
        .monetra-btn:hover {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.danger {
          background: rgba(239, 68, 68, 0.12);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }
        .monetra-btn.danger:hover {
          background: rgba(239, 68, 68, 0.25);
        }
        .monetra-btn.outline {
          background: transparent;
          border: 1px solid rgba(255,255,255,0.08);
        }
        .monetra-btn.outline:hover {
          background: rgba(255,255,255,0.04);
        }
        .monetra-btn.warning-outline {
          background: rgba(245, 158, 11, 0.08);
          border: 1px solid rgba(245, 158, 11, 0.2);
          color: #fbbf24;
        }
        .monetra-btn.warning-outline:hover {
          background: rgba(245, 158, 11, 0.15);
          color: #fcd34d;
          transform: translateY(-2px);
        }

        /* Tableau */
        .table-eval {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0 8px;
          font-size: 0.9rem;
        }
        .table-eval thead th {
          background: rgba(255,255,255,0.03);
          padding: 12px 12px;
          border-bottom: 1px solid rgba(255,255,255,0.05);
          color: var(--text-secondary);
          font-weight: 500;
          text-transform: uppercase;
          font-size: 0.7rem;
          letter-spacing: 0.5px;
          cursor: pointer;
          transition: color 0.2s;
        }
        .table-eval thead th:hover {
          color: var(--text-primary);
        }
        .table-eval tbody tr {
          background: rgba(255,255,255,0.02);
          border-radius: 10px;
          transition: background 0.2s;
        }
        .table-eval tbody tr:hover {
          background: rgba(255,255,255,0.04);
        }
        .table-eval tbody td {
          padding: 12px 12px;
          vertical-align: middle;
          border-bottom: 1px solid rgba(255,255,255,0.03);
        }
        .table-eval .actions-cell {
          display: flex;
          gap: 6px;
        }
        .table-eval .actions-cell button {
          background: transparent;
          border: none;
          color: var(--text-muted);
          transition: color 0.2s, transform 0.2s;
          padding: 6px 8px;
          border-radius: 8px;
        }
        .table-eval .actions-cell button:hover {
          color: var(--text-primary);
          background: rgba(255,255,255,0.04);
          transform: scale(1.05);
        }
        .table-eval .actions-cell .btn-edit:hover { color: var(--accent-blue); }
        .table-eval .actions-cell .btn-delete:hover { color: #f87171; }

        .search-box {
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 16px;
          gap: 10px;
          max-width: 280px;
        }
        .search-box input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          padding: 8px 0;
          outline: none;
          width: 100%;
        }
        .search-box input::placeholder {
          color: var(--text-muted);
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 1.5rem;
          justify-content: flex-end;
        }
        .pagination-controls button {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 6px 16px;
          color: var(--text-secondary);
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .pagination-controls button:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          color: var(--text-primary);
        }
        .pagination-controls button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
        .pagination-controls span {
          color: var(--text-secondary);
          font-size: 0.9rem;
        }

        /* Selecteur employé personnalisé (dropdown) */
        .employee-selector {
          position: relative;
        }
        .employee-selector .input-glass {
          cursor: pointer;
        }
        .employee-selector .dropdown-list {
          position: absolute;
          top: calc(100% + 6px);
          left: 0;
          right: 0;
          background: rgba(10, 20, 45, 0.96);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(59, 130, 246, 0.15);
          border-radius: 16px;
          max-height: 260px;
          overflow-y: auto;
          z-index: 1050;
          box-shadow: 0 20px 60px rgba(0,0,0,0.6);
          padding: 8px 0;
        }
        .employee-selector .dropdown-list .search-input {
          position: sticky;
          top: 0;
          background: inherit;
          padding: 8px 16px;
          border-bottom: 1px solid rgba(255,255,255,0.05);
        }
        .employee-selector .dropdown-list .search-input input {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 8px 16px;
          color: var(--text-primary);
          outline: none;
        }
        .employee-selector .dropdown-list .search-input input::placeholder {
          color: var(--text-muted);
        }
        .employee-selector .dropdown-list .emp-item {
          padding: 10px 16px;
          cursor: pointer;
          transition: background 0.15s;
          display: flex;
          align-items: center;
          gap: 10px;
          color: var(--text-secondary);
        }
        .employee-selector .dropdown-list .emp-item:hover {
          background: rgba(59, 130, 246, 0.08);
          color: var(--text-primary);
        }
        .employee-selector .dropdown-list .emp-item .matricule {
          font-size: 0.75rem;
          color: var(--text-muted);
          margin-left: auto;
        }

        .toast-container {
          position: fixed;
          top: 24px;
          right: 24px;
          z-index: 9999;
          max-width: 440px;
        }
        .toast-item {
          background: rgba(10, 20, 45, 0.8);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: 16px;
          padding: 14px 20px;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.4);
          animation: slideIn 0.35s ease;
          position: relative;
          overflow: hidden;
          margin-bottom: 12px;
        }
        .toast-item.success { border-left: 4px solid var(--accent-blue); }
        .toast-item.warning { border-left: 4px solid #f59e0b; }
        .toast-item.danger { border-left: 4px solid #ef4444; }
        .toast-progress {
          position: absolute;
          bottom: 0;
          left: 0;
          height: 3px;
          background: currentColor;
          animation: progressShrink 5s linear forwards;
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(40px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes progressShrink {
          from { width: 100%; }
          to { width: 0%; }
        }
        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-muted);
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          margin-left: auto;
          transition: color 0.2s;
        }
        .btn-close-custom:hover { color: var(--text-primary); }

        /* ============================================================
           MODAL PREMIUM
           ============================================================ */
        .modal-glass .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px) saturate(1.2);
          -webkit-backdrop-filter: blur(30px) saturate(1.2);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          box-shadow: 0 40px 90px rgba(0,0,0,0.8), 0 0 0 1px rgba(59, 130, 246, 0.04) inset;
          overflow: hidden;
        }

        .modal-glass .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
          padding: 1.8rem 2.2rem;
          background: rgba(0,0,0,0.15);
        }

        .modal-title-wrapper {
          display: flex;
          align-items: center;
          gap: 14px;
        }
        .modal-title-icon {
          width: 44px;
          height: 44px;
          border-radius: 14px;
          background: var(--accent-blue-dim);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.3rem;
          color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }
        .modal-title-text {
          font-size: 1.3rem;
          font-weight: 600;
          letter-spacing: -0.3px;
          background: linear-gradient(135deg, #e0e7ff 60%, #94a3b8);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .modal-title-sub {
          font-size: 0.85rem;
          color: var(--text-muted);
          font-weight: 400;
          margin-top: 2px;
        }

        .modal-glass .modal-body {
          padding: 2.2rem 2.2rem 1.8rem;
        }

        .modal-glass .modal-footer {
          border-top: 1px solid rgba(59, 130, 246, 0.06);
          padding: 1.2rem 2.2rem;
          background: rgba(0,0,0,0.08);
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .form-group-premium {
          margin-bottom: 1.5rem;
        }
        .form-group-premium label {
          display: block;
          margin-bottom: 6px;
          font-size: 0.8rem;
          font-weight: 500;
          color: var(--text-secondary);
          letter-spacing: 0.3px;
          text-transform: uppercase;
        }
        .form-group-premium label .required {
          color: #f87171;
          margin-left: 2px;
        }
        .form-group-premium label .label-icon {
          color: var(--accent-blue);
          opacity: 0.6;
          margin-right: 6px;
        }

        .input-glass {
          display: flex;
          align-items: center;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: 14px;
          transition: all 0.25s ease;
        }
        .input-glass:focus-within {
          border-color: var(--accent-blue);
          background: rgba(255, 255, 255, 0.05);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08), 0 4px 20px rgba(59, 130, 246, 0.05);
        }
        .input-glass select,
        .input-glass input,
        .input-glass textarea {
          flex: 1;
          background: transparent;
          border: none;
          padding: 12px 16px;
          color: var(--text-primary);
          outline: none;
          font-family: inherit;
          font-size: 0.95rem;
        }
        .input-glass textarea {
          min-height: 80px;
          resize: vertical;
        }
        .input-glass select {
          appearance: none;
          background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: right 12px center;
          background-size: 16px;
          cursor: pointer;
        }
        .input-glass select option {
          background: #0b1428;
          color: var(--text-primary);
        }
        .input-glass.error {
          border-color: #f87171;
          background: rgba(239, 68, 68, 0.03);
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.06);
        }

        .criteria-section {
          margin-top: 1.5rem;
          padding: 1rem 1.2rem;
          background: rgba(255,255,255,0.02);
          border-radius: 16px;
          border: 1px solid rgba(255,255,255,0.04);
        }
        .criteria-section h6 {
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--accent-blue);
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .criteria-row {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 10px;
          padding: 8px 12px;
          background: rgba(255,255,255,0.02);
          border-radius: 10px;
          transition: background 0.2s;
        }
        .criteria-row:hover {
          background: rgba(255,255,255,0.04);
        }
        .criteria-row span {
          width: 180px;
          font-size: 0.9rem;
          color: var(--text-secondary);
          font-weight: 500;
        }
        .rating-buttons {
          display: flex;
          gap: 6px;
          align-items: center;
        }
        .rating-buttons button {
          background: transparent;
          border: none;
          color: #4a4a52;
          font-size: 1.6rem;
          cursor: pointer;
          transition: all 0.2s ease;
          padding: 0 2px;
          line-height: 1;
          transform-origin: center;
        }
        .rating-buttons button.active {
          color: #fbbf24;
          transform: scale(1.1);
        }
        .rating-buttons button:hover {
          color: #fbbf24;
          transform: scale(1.15);
        }
        .rating-buttons button:active {
          transform: scale(0.9);
        }

        .score-summary {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1.5rem;
          padding: 12px 20px;
          background: rgba(59, 130, 246, 0.04);
          border-radius: 12px;
          border: 1px solid rgba(59, 130, 246, 0.06);
          margin-top: 1rem;
        }
        .score-summary .avg-score {
          font-size: 2rem;
          font-weight: 700;
          color: var(--accent-blue);
        }
        .score-summary .avg-label {
          font-size: 0.8rem;
          color: var(--text-secondary);
        }

        .btn-modal-cancel,
        .btn-modal-submit {
          padding: 10px 28px;
          border-radius: 30px;
          font-weight: 500;
          transition: all 0.25s ease;
          border: none;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .btn-modal-cancel {
          background: rgba(255,255,255,0.04);
          color: var(--text-secondary);
          border: 1px solid rgba(255,255,255,0.06);
        }
        .btn-modal-cancel:hover {
          background: rgba(255,255,255,0.08);
          color: var(--text-primary);
          transform: translateY(-2px);
        }
        .btn-modal-submit {
          background: var(--accent-blue);
          color: #fff;
          box-shadow: 0 6px 24px rgba(59, 130, 246, 0.2);
        }
        .btn-modal-submit:hover {
          background: var(--accent-dark-blue);
          transform: translateY(-2px);
          box-shadow: 0 8px 32px rgba(59, 130, 246, 0.3);
        }
        .btn-modal-submit:disabled {
          opacity: 0.5;
          pointer-events: none;
        }

        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-warning { color: #fbbf24; }
        .text-success { color: var(--accent-blue); }
        .text-danger { color: #ef4444; }
        .text-white { color: var(--text-primary); }
        .fw-bold { font-weight: 600; }
        .opacity-30 { opacity: 0.3; }
        .fst-italic { font-style: italic; }
        .small { font-size: 0.85rem; }

        @media (max-width: 768px) {
          .eval-container { padding: 16px; }
          .modal-glass .modal-header { padding: 1.2rem 1.5rem; }
          .modal-glass .modal-body { padding: 1.5rem; }
          .modal-glass .modal-footer { padding: 1rem 1.5rem; flex-direction: column-reverse; }
          .modal-glass .modal-footer button { width: 100%; justify-content: center; }
          .criteria-row { flex-wrap: wrap; }
          .criteria-row span { width: 100%; }
          .table-eval thead { display: none; }
          .table-eval tbody tr { display: block; margin-bottom: 16px; background: rgba(255,255,255,0.02); border-radius: 12px; padding: 12px; }
          .table-eval tbody td { display: block; border: none; padding: 6px 0; }
          .table-eval tbody td:before { content: attr(data-label); font-weight: 600; color: var(--text-secondary); display: inline-block; width: 100px; }
          .table-eval .actions-cell { display: flex; gap: 8px; margin-top: 8px; }
          .search-box { max-width: 100%; }
        }
      `}</style>

      <div className="eval-container">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h2 className="display-6 fw-bold mb-1">
              <FontAwesomeIcon icon={faClipboardList} className="text-success me-2" />
              Évaluations des employés
            </h2>
            <p className="text-secondary m-0">Gérez les évaluations de performance</p>
          </div>
          <button className="monetra-btn primary" onClick={openCreateModal} disabled={!managerId}>
            <FontAwesomeIcon icon={faPlus} /> Nouvelle évaluation
          </button>
        </div>

        <div className="glass-card">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
            <h5>
              <FontAwesomeIcon icon={faChartBar} className="me-2" />
              Historique de mes évaluations
            </h5>
            <div className="d-flex gap-2 flex-wrap">
              <div className="search-box">
                <FontAwesomeIcon icon={faSearch} className="text-muted" />
                <input
                  type="text"
                  placeholder="Rechercher un employé..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button
                className="monetra-btn outline"
                onClick={() => fetchEvaluations(true)}
                disabled={loading || refreshing}
              >
                <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} /> Actualiser
              </button>
              <button
                className="monetra-btn warning-outline"
                onClick={clearCache}
                disabled={clearingCache}
                title="Vider le cache local (forcer un rechargement complet depuis l'API)"
              >
                <FontAwesomeIcon icon={faEraser} spin={clearingCache} /> Vider le cache
              </button>
            </div>
          </div>

          {loading && (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="text-secondary mt-2">Chargement...</p>
            </div>
          )}

          {!loading && filteredEvaluations.length === 0 && (
            <div className="text-center py-5">
              <FontAwesomeIcon icon={faClipboardList} size="3x" className="text-muted mb-3 opacity-30" />
              <p className="text-secondary">Aucune évaluation trouvée.</p>
              <p className="text-muted small">Commencez par évaluer un employé.</p>
            </div>
          )}

          {!loading && filteredEvaluations.length > 0 && (
            <>
              <div className="table-responsive">
                <table className="table-eval">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('employe')}>
                        Employé
                        {sortField === 'employe' && (
                          <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortUp : faSortDown} className="ms-1" />
                        )}
                      </th>
                      <th onClick={() => handleSort('date_evaluation')}>
                        Date
                        {sortField === 'date_evaluation' && (
                          <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortUp : faSortDown} className="ms-1" />
                        )}
                      </th>
                      <th onClick={() => handleSort('note_globale')}>
                        Note
                        {sortField === 'note_globale' && (
                          <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortUp : faSortDown} className="ms-1" />
                        )}
                      </th>
                      <th>Commentaire</th>
                      <th style={{ width: 100 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedEvaluations.map(evalItem => {
                      const emp = getEmployeeInfo(evalItem.employe_id);
                      return (
                        <tr key={evalItem.id}>
                          <td data-label="Employé">
                            <strong className="text-white">
                              {emp ? `${emp.prenom} ${emp.nom}` : `#${evalItem.employe_id}`}
                            </strong>
                            {emp?.matricule && <span className="text-muted small ms-2">({emp.matricule})</span>}
                            {emp?.poste && <span className="text-secondary small ms-2">- {emp.poste}</span>}
                          </td>
                          <td data-label="Date">
                            <FontAwesomeIcon icon={faCalendarAlt} className="text-muted me-2" />
                            {evalItem.date_evaluation}
                          </td>
                          <td data-label="Note">
                            {renderStars(evalItem.note_globale)}
                          </td>
                          <td data-label="Commentaire" className="text-secondary small fst-italic">
                            {evalItem.commentaire || '—'}
                          </td>
                          <td data-label="Actions">
                            <div className="actions-cell">
                              <button className="btn-edit" onClick={() => openEditModal(evalItem)} title="Modifier">
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button className="btn-delete" onClick={() => confirmDelete(evalItem)} title="Supprimer">
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="pagination-controls">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <FontAwesomeIcon icon={faChevronLeft} /> Précédent
                </button>
                <span>
                  Page {currentPage} sur {totalPages || 1}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  Suivant <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ============================================================
          MODAL PREMIUM – Ajout / Modification
          ============================================================ */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered backdrop="static" className="modal-glass">
        <Modal.Header closeButton className="glass-modal">
          <Modal.Title>
            <div className="modal-title-wrapper">
              <div className="modal-title-icon">
                <FontAwesomeIcon icon={currentEval ? faEdit : faPlus} />
              </div>
              <div>
                <div className="modal-title-text">
                  {currentEval ? "Modifier l'évaluation" : 'Nouvelle évaluation'}
                </div>
                <div className="modal-title-sub">
                  {currentEval ? `#${currentEval.id}` : "Attribuez une note à un employé"}
                </div>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="glass-modal">
          <Form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-8">
                <div className="form-group-premium employee-selector" ref={employeeDropdownRef}>
                  <label>
                    <FontAwesomeIcon icon={faUserTie} className="label-icon" />
                    Employé <span className="required">*</span>
                  </label>
                  <div
                    className={`input-glass ${!currentEval ? '' : 'opacity-50'}`}
                    onClick={() => !currentEval && setShowEmployeeDropdown(!showEmployeeDropdown)}
                  >
                    <input
                      type="text"
                      placeholder="Rechercher un employé..."
                      value={employeeSearch}
                      onChange={(e) => {
                        setEmployeeSearch(e.target.value);
                        setShowEmployeeDropdown(true);
                        if (e.target.value === '') {
                          setForm(prev => ({ ...prev, employe_id: '' }));
                        }
                      }}
                      disabled={!!currentEval}
                      className="form-control-plaintext"
                      style={{ color: 'var(--text-primary)' }}
                    />
                    <FontAwesomeIcon icon={faSearch} className="text-muted me-2" />
                  </div>
                  {showEmployeeDropdown && !currentEval && (
                    <div className="dropdown-list">
                      <div className="search-input">
                        <input
                          type="text"
                          placeholder="Filtrer..."
                          value={employeeSearch}
                          onChange={(e) => setEmployeeSearch(e.target.value)}
                          autoFocus
                        />
                      </div>
                      {filteredEmployees.length === 0 ? (
                        <div className="emp-item text-muted">Aucun employé trouvé</div>
                      ) : (
                        filteredEmployees.map(emp => (
                          <div
                            key={emp.id}
                            className="emp-item"
                            onClick={() => selectEmployee(emp.id)}
                          >
                            <span>{emp.prenom} {emp.nom}</span>
                            <span className="matricule">{emp.matricule}</span>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group-premium">
                  <label>
                    <FontAwesomeIcon icon={faCalendarAlt} className="label-icon" />
                    Date <span className="required">*</span>
                  </label>
                  <div className="input-glass">
                    <Form.Control
                      type="date"
                      value={form.date_evaluation}
                      onChange={(e) => setForm(prev => ({ ...prev, date_evaluation: e.target.value }))}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="criteria-section">
              <h6>
                <FontAwesomeIcon icon={faAward} />
                Critères d'évaluation (note de 1 à 5)
              </h6>

              {criteria.length === 0 ? (
                <p className="text-muted">Chargement des critères...</p>
              ) : (
                criteria.map(crit => (
                  <div key={crit.id} className="criteria-row">
                    <span>{crit.libelle}</span>
                    <div className="rating-buttons">
                      {[1,2,3,4,5].map(star => (
                        <button
                          key={star}
                          type="button"
                          className={(form.scores[crit.id] >= star) ? 'active' : ''}
                          onClick={() => handleScoreChange(crit.id, star)}
                          title={`${star} étoile(s)`}
                        >
                          <FontAwesomeIcon icon={faStar} />
                        </button>
                      ))}
                    </div>
                    <span className="ms-2 text-muted small" style={{ minWidth: 24 }}>
                      {form.scores[crit.id] || 0}/5
                    </span>
                  </div>
                ))
              )}

              {criteria.length > 0 && (
                <div className="score-summary">
                  <div className="d-flex align-items-center gap-3">
                    <FontAwesomeIcon icon={faMedal} className="text-warning" size="2x" />
                    <div>
                      <div className="avg-score">
                        {Object.values(form.scores).length > 0
                          ? (Object.values(form.scores).reduce((a,b) => a + b, 0) / Object.values(form.scores).filter(v => v > 0).length || 0).toFixed(1)
                          : '0.0'}
                      </div>
                      <div className="avg-label">Note moyenne</div>
                    </div>
                  </div>
                  <div className="text-muted small text-center">
                    <FontAwesomeIcon icon={faArrowRight} className="me-1" />
                    {Object.values(form.scores).filter(v => v > 0).length} / {Object.values(form.scores).length} critères notés
                  </div>
                </div>
              )}
            </div>

            <div className="form-group-premium mt-3">
              <label>
                <FontAwesomeIcon icon={faCommentAlt} className="label-icon" />
                Commentaire
              </label>
              <div className="input-glass">
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={form.commentaire}
                  onChange={(e) => setForm(prev => ({ ...prev, commentaire: e.target.value }))}
                  placeholder="Points forts, axes d'amélioration..."
                />
              </div>
            </div>
          </Form>
        </Modal.Body>

        <Modal.Footer className="glass-modal">
          <button className="btn-modal-cancel" onClick={() => setShowModal(false)} disabled={submitting}>
            <FontAwesomeIcon icon={faTimes} /> Annuler
          </button>
          <button className="btn-modal-submit" onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <Spinner as="span" animation="border" size="sm" />
            ) : (
              <FontAwesomeIcon icon={faSave} />
            )}
            {currentEval ? 'Mettre à jour' : 'Enregistrer'}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ============================================================
          MODAL SUPPRESSION
          ============================================================ */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered backdrop="static" className="modal-glass" size="sm">
        <Modal.Header closeButton className="glass-modal">
          <Modal.Title>
            <div className="modal-title-wrapper">
              <div className="modal-title-icon" style={{ background: 'rgba(239,68,68,0.12)', color: '#f87171' }}>
                <FontAwesomeIcon icon={faTrashAlt} />
              </div>
              <div>
                <div className="modal-title-text" style={{ color: '#f87171', background: 'none', WebkitTextFillColor: '#f87171' }}>
                  Confirmer la suppression
                </div>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="glass-modal text-center py-4">
          <p className="text-secondary">Supprimer définitivement cette évaluation ?</p>
          {deleteTarget && (
            <p className="fw-bold text-white">
              {getEmployeeInfo(deleteTarget.employe_id)?.prenom} {getEmployeeInfo(deleteTarget.employe_id)?.nom}
              <br />
              <span className="text-muted small">{deleteTarget.date_evaluation}</span>
            </p>
          )}
          <small className="text-muted">Cette action est irréversible.</small>
        </Modal.Body>
        <Modal.Footer className="glass-modal justify-content-center">
          <button className="btn-modal-cancel" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            Annuler
          </button>
          <button
            className="btn-modal-submit"
            onClick={handleDelete}
            disabled={deleting}
            style={{ background: '#f87171', color: '#fff', boxShadow: '0 6px 24px rgba(239,68,68,0.3)' }}
          >
            {deleting ? <Spinner as="span" animation="border" size="sm" /> : <FontAwesomeIcon icon={faTrash} />}
            Supprimer
          </button>
        </Modal.Footer>
      </Modal>

      {/* Toast */}
      {toast && (
        <div className="toast-container">
          <ToastMessage toast={toast} />
        </div>
      )}
    </>
  );
};

export default EvaluationEmployes;